import { useState, useEffect } from "react";
import Alert from "@mui/material/Alert";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { alpha, useTheme } from "@mui/material/styles";
import { useStockLocations } from "../../api/stocklocations";
import { useStockRelocation } from "../../api/stockrelocation";
import { ProductStock, StockLocation } from "../../shared/types";

interface RelocationDialogProps {
    open: boolean;
    onClose: () => void;
    onSuccess: () => void;
    stockItem: ProductStock;
}

export default function RelocationDialog({ open, onClose, onSuccess, stockItem }: RelocationDialogProps) {
    const theme = useTheme();
    const { stockLocations, loading: locationsLoading } = useStockLocations();
    const { requestRelocation, submitting, error } = useStockRelocation();

    const [selectedLocation, setSelectedLocation] = useState<StockLocation | null>(null);
    const [selectedLevel, setSelectedLevel] = useState<number | null>(null);

    useEffect(() => {
        if (!open) {
            setSelectedLocation(null);
            setSelectedLevel(null);
        }
    }, [open]);

    useEffect(() => {
        setSelectedLevel(null);
    }, [selectedLocation]);

    const canSubmit = selectedLocation !== null && selectedLevel !== null && !submitting;

    async function handleSubmit() {
        if (!selectedLocation || selectedLevel === null) return;

        const success = await requestRelocation(
            stockItem.id,
            selectedLocation.id,
            selectedLevel
        );

        if (success) {
            onSuccess();
            onClose();
        }
    }

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>Request Stock Move</DialogTitle>
            <DialogContent>
                <Stack spacing={3} sx={{ mt: 1 }}>
                    <Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            Moving <strong>{stockItem.product.code}</strong> from{" "}
                            <strong>{stockItem.location.name} (Level {stockItem.level})</strong>
                        </Typography>
                    </Box>

                    {error && (
                        <Alert severity="error">{error}</Alert>
                    )}

                    <Autocomplete
                        options={stockLocations}
                        loading={locationsLoading}
                        value={selectedLocation}
                        onChange={(_, value) => setSelectedLocation(value)}
                        getOptionLabel={(option) => `${option.name} (${option.warehouse.name})`}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        filterOptions={(options, { inputValue }) => {
                            const q = inputValue.toLowerCase();
                            return options.filter(
                                (o) =>
                                    o.name.toLowerCase().includes(q) ||
                                    o.warehouse.name.toLowerCase().includes(q)
                            );
                        }}
                        sx={{
                            "& .MuiIconButton-root": {
                                border: "none",
                                background: "none",
                                backgroundColor: "transparent",
                                "&:hover": {
                                    background: "none",
                                    backgroundColor: "transparent",
                                },
                            },
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                placeholder="Search locations..."
                                hiddenLabel
                                label=""
                            />
                        )}
                    />

                    {selectedLocation && (
                        <Box>
                            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: "block" }}>
                                Select level
                            </Typography>
                            <Stack spacing={0}>
                                {Array.from({ length: selectedLocation.levels }, (_, i) => selectedLocation.levels - i).map(
                                    (level) => {
                                        const isSelected = selectedLevel === level;
                                        return (
                                            <Box
                                                key={level}
                                                onClick={() => setSelectedLevel(level)}
                                                sx={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    height: 44,
                                                    border: "1px solid",
                                                    borderColor: isSelected
                                                        ? theme.palette.primary.main
                                                        : theme.palette.divider,
                                                    borderBottom: level === 1 ? "1px solid" : "none",
                                                    borderBottomColor: isSelected
                                                        ? theme.palette.primary.main
                                                        : theme.palette.divider,
                                                    "&:first-of-type": {
                                                        borderTopLeftRadius: theme.shape.borderRadius,
                                                        borderTopRightRadius: theme.shape.borderRadius,
                                                    },
                                                    "&:last-of-type": {
                                                        borderBottom: "1px solid",
                                                        borderBottomColor: isSelected
                                                            ? theme.palette.primary.main
                                                            : theme.palette.divider,
                                                        borderBottomLeftRadius: theme.shape.borderRadius,
                                                        borderBottomRightRadius: theme.shape.borderRadius,
                                                    },
                                                    backgroundColor: isSelected
                                                        ? theme.palette.primary.main
                                                        : "transparent",
                                                    color: isSelected
                                                        ? "#fff"
                                                        : theme.palette.text.secondary,
                                                    cursor: "pointer",
                                                    transition: "all 120ms ease",
                                                    "&:hover": isSelected
                                                        ? {}
                                                        : {
                                                              backgroundColor: alpha(
                                                                  theme.palette.primary.main,
                                                                  0.08
                                                              ),
                                                              borderColor: alpha(
                                                                  theme.palette.primary.main,
                                                                  0.4
                                                              ),
                                                              color: theme.palette.primary.main,
                                                          },
                                                }}
                                            >
                                                <Typography
                                                    variant="body2"
                                                    fontWeight={isSelected ? 700 : 500}
                                                >
                                                    Level {level}
                                                </Typography>
                                            </Box>
                                        );
                                    }
                                )}
                            </Stack>
                        </Box>
                    )}
                </Stack>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={onClose} disabled={submitting}>
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={!canSubmit}
                    startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : undefined}
                >
                    {submitting ? "Submitting..." : "Request Move"}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
